import React from 'react';
import { createRoot } from 'react-dom/client';
import { AppKitNetwork } from '@reown/appkit/networks';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SolanaAdapter } from '@reown/appkit-adapter-solana';
import { ConnectionProvider } from '@solana/wallet-adapter-react';
import { AppKitProvider } from '@reown/appkit/react';
import type { CustomRpcUrlMap } from '@reown/appkit-common';
import { ApiController } from '@reown/appkit-controllers';

// Initialize Sentry BEFORE any other imports might throw errors
// eslint-disable-next-line import/order
import { initSentry } from './lib/sentry';

initSentry();

import './utils/dayjs-config';

import { initResponsive } from './lib/responsive.ts';
import { AuthProvider } from './context/AuthContext.tsx';
import { PhantomProvider } from './context/PhantomContext.tsx';
import { SentryErrorBoundary } from './components/SentryErrorBoundary';
import { SwapDebugMenu } from './components/SwapDebugMenu';
import AppRouter from './AppRouter.tsx';
import config from './config/index.ts';
import './index.css';
import { getGateWalletAdapter } from './adapters/GateWalletAdapter.ts';

initResponsive();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const projectId = config.WALLET_CONNECT_PROJECT_ID;
const metadata = config.WALLET_CONNECT_METADATA;
const networks: [AppKitNetwork, ...AppKitNetwork[]] = [config.SOLANA_NETWORK];

const customRpcUrls: CustomRpcUrlMap = {
  [`solana:${config.SOLANA_NETWORK.id}`]: [
    {
      url: config.SOLANA_RPC_URL,
    },
  ],
};

const solanaWeb3JsAdapter = new SolanaAdapter({
  wallets: [getGateWalletAdapter()],
});

const BACKPACK_WALLET_ID = '2bd8c14e035c2d48f184aaa168559e86b0e3433228d3c4075900a221785019b0';
const SOLFLARE_WALLET_ID = '1ca0bdd4747578705b1939af023d120677c64fe6ca76add81fda36e350605e79';

// eslint-disable-next-line react-refresh/only-export-components
function WalletPrefetcher() {
  React.useEffect(() => {
    ApiController.prefetch({
      fetchConnectorImages: false,
      fetchNetworkImages: false,
    }).catch(() => {
      // Ignore prefetch errors; AppKit will handle retries when needed.
    });
  }, []);

  return null;
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <SentryErrorBoundary>
      <ConnectionProvider endpoint={config.SOLANA_RPC_URL}>
        <AppKitProvider
          networks={networks}
          projectId={projectId}
          metadata={metadata}
          adapters={[solanaWeb3JsAdapter]}
          features={{ analytics: true, allWallets: true }}
          customRpcUrls={customRpcUrls}
          featuredWalletIds={[SOLFLARE_WALLET_ID, BACKPACK_WALLET_ID]}
        >
          <PhantomProvider>
            <WalletPrefetcher />
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <AppRouter />
                <SwapDebugMenu />
              </AuthProvider>
            </QueryClientProvider>
          </PhantomProvider>
        </AppKitProvider>
      </ConnectionProvider>
    </SentryErrorBoundary>
  </React.StrictMode>
);
